import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import { PageHeader } from '@routes-plan/ui';
import { currentLang, useT } from '../../i18n';
import type { HelpMeta } from './HelpList';
import GuideContent, { type HelpGuideData } from '../../components/help/GuideContent';

type Guide = HelpGuideData & HelpMeta;

/**
 * Una guía con su video. El video llega con un enlace firmado que vence en minutos: si vence mientras la
 * persona tiene la pantalla abierta, se pide uno nuevo al reproducir.
 */
const HelpGuide: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const { topic = '' } = useParams();
  const [guide, setGuide] = useState<Guide | null>(null);
  const [error, setError] = useState<'notFound' | 'load' | null>(null);

  const load = React.useCallback(() => {
    setError(null);
    return axios
      .get(`help/${encodeURIComponent(topic)}`, { params: { lang: currentLang() } })
      .then(({ data }) => {
        setGuide(data);
        document.title = `${data.title} · ${t('help.title')}`;
      })
      .catch((e) => setError(e?.response?.status === 404 ? 'notFound' : 'load'));
  }, [topic, t]);

  useEffect(() => {
    setGuide(null);
    void load();
    window.scrollTo?.(0, 0);
  }, [load]);

  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: 960, mx: 'auto', pb: 6 }}>
        {(error || !guide) && (
          <PageHeader
            title={t('help.title')}
            actions={
              <Button variant="outlined" startIcon={<ArrowBackOutlined />} onClick={() => navigate('/ayuda')}>
                {t('help.back')}
              </Button>
            }
          />
        )}
        {error && <Alert severity={error === 'notFound' ? 'info' : 'error'}>{t(error === 'notFound' ? 'help.notFound' : 'help.loadError')}</Alert>}
        {!guide && !error && (
          <>
            <Skeleton variant="rounded" height={280} sx={{ my: 2 }} />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="80%" />
          </>
        )}
        {guide && (
          <>
            <PageHeader
              title={guide.title}
              breadcrumbs={[{ label: t('help.title'), href: '#/ayuda', onClick: () => navigate('/ayuda') }, { label: guide.title }]}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: -1.5 }}>
              {guide.audience && <Chip size="small" label={guide.audience} />}
              {guide.platforms && <Chip size="small" label={guide.platforms} variant="outlined" />}
              {guide.updated && <Chip size="small" label={t('help.updated', { date: guide.updated })} variant="outlined" />}
            </Box>

            <GuideContent guide={guide} onVideoExpired={() => void load()} />
          </>
        )}
    </Box>
  );
};

export default HelpGuide;
